/**
 * ETL Schema Propagation Engine
 *
 * Design-time column awareness for the ETL pipeline designer.
 * Models Spark's StructType/StructField system — each node carries
 * an input schema and an output schema. When a node's config or
 * connections change, schemas propagate downstream through the DAG.
 *
 * Type system mirrors PySpark 3.5:
 *   StringType, IntegerType, LongType, FloatType, DoubleType,
 *   DecimalType(p,s), BooleanType, DateType, TimestampType,
 *   BinaryType, ArrayType, MapType, StructType, VectorType
 */

// =============================================================================
// SPARK DATA TYPES
// =============================================================================

export const SPARK_TYPES = {
  STRING:    'StringType',
  INTEGER:   'IntegerType',
  LONG:      'LongType',
  FLOAT:     'FloatType',
  DOUBLE:    'DoubleType',
  DECIMAL:   'DecimalType',
  BOOLEAN:   'BooleanType',
  DATE:      'DateType',
  TIMESTAMP: 'TimestampType',
  BINARY:    'BinaryType',
  ARRAY:     'ArrayType',
  MAP:       'MapType',
  STRUCT:    'StructType',
  VECTOR:    'VectorType',   // MLlib DenseVector / SparseVector
}

/** Map shorthand type strings (from config inputs) to Spark type names */
const TYPE_ALIASES = {
  string:    SPARK_TYPES.STRING,
  str:       SPARK_TYPES.STRING,
  varchar:   SPARK_TYPES.STRING,
  text:      SPARK_TYPES.STRING,
  int:       SPARK_TYPES.INTEGER,
  integer:   SPARK_TYPES.INTEGER,
  long:      SPARK_TYPES.LONG,
  bigint:    SPARK_TYPES.LONG,
  float:     SPARK_TYPES.FLOAT,
  double:    SPARK_TYPES.DOUBLE,
  decimal:   SPARK_TYPES.DECIMAL,
  bool:      SPARK_TYPES.BOOLEAN,
  boolean:   SPARK_TYPES.BOOLEAN,
  date:      SPARK_TYPES.DATE,
  timestamp: SPARK_TYPES.TIMESTAMP,
  datetime:  SPARK_TYPES.TIMESTAMP,
  binary:    SPARK_TYPES.BINARY,
  bytes:     SPARK_TYPES.BINARY,
  array:     SPARK_TYPES.ARRAY,
  map:       SPARK_TYPES.MAP,
  struct:    SPARK_TYPES.STRUCT,
  vector:    SPARK_TYPES.VECTOR,
}

/**
 * Resolve a user-supplied type string to a Spark type name.
 * Handles aliases, decimal(p,s) syntax, and pass-through for already-resolved names.
 */
export function resolveType(raw) {
  if (!raw) return SPARK_TYPES.STRING
  const lower = raw.toLowerCase().trim()
  // Handle decimal(p,s) — keep precision/scale info
  if (lower.startsWith('decimal')) return SPARK_TYPES.DECIMAL
  return TYPE_ALIASES[lower] || raw
}

// =============================================================================
// SCHEMA OBJECT — StructField / StructType equivalents
// =============================================================================

/**
 * Create a single column definition (StructField).
 * @param {string} name     - Column name
 * @param {string} type     - Spark type name (from SPARK_TYPES)
 * @param {boolean} nullable - Whether column allows nulls (default true)
 * @returns {{ name: string, type: string, nullable: boolean }}
 */
export function createColumn(name, type = SPARK_TYPES.STRING, nullable = true) {
  return { name, type: resolveType(type), nullable }
}

/**
 * Create a schema object (StructType).
 * @param {Array} columns   - Array of column objects
 * @param {string} source   - 'inferred' | 'manual' | 'propagated'
 * @returns {{ columns: Array, source: string }}
 */
export function createSchema(columns = [], source = 'propagated') {
  return {
    columns: columns.map(c => ({
      name: c.name,
      type: resolveType(c.type),
      nullable: c.nullable !== false,
    })),
    source,
  }
}

/** Empty schema — no columns known */
export const EMPTY_SCHEMA = createSchema([], 'propagated')

/** Get column names from a schema */
export function columnNames(schema) {
  if (!schema?.columns) return []
  return schema.columns.map(c => c.name)
}

/** Find a column by name (case-insensitive) */
export function findColumn(schema, name) {
  if (!schema?.columns || !name) return null
  const lower = name.toLowerCase()
  return schema.columns.find(c => c.name.toLowerCase() === lower) || null
}

// =============================================================================
// SAFE CONFIG PARSING
// =============================================================================

/** Parse a JSON string config value, return fallback on failure */
function parseConfig(raw, fallback) {
  if (!raw) return fallback
  if (typeof raw === 'object') return raw
  try { return JSON.parse(raw) } catch { return fallback }
}

// =============================================================================
// SCHEMA COMPUTATION RULES — per node type
// =============================================================================

/**
 * Compute the output schema for a given node.
 *
 * @param {string} nodeType     - ETL node type ID (e.g., 'etl-transform-filter')
 * @param {Object} inputSchema  - Input schema (from parent node's output)
 * @param {Object} config       - Node's current config values
 * @param {Object} [rightSchema] - Second input schema (for Join, Union, etc.)
 * @returns {Object} Output schema
 */
export function computeOutputSchema(nodeType, inputSchema, config = {}, rightSchema = null) {
  const input = inputSchema || EMPTY_SCHEMA
  const cols = input.columns || []

  // ------------------------------------------------------------------
  // PASS-THROUGH nodes — output schema === input schema
  // ------------------------------------------------------------------
  const PASS_THROUGH = [
    // Transforms
    'etl-transform-filter',
    'etl-transform-sort',
    'etl-transform-deduplicate',
    'etl-transform-split',
    // Data Prep
    'etl-prep-fill-na',
    'etl-prep-drop-na',
    'etl-prep-sample',
    'etl-prep-limit',
    'etl-prep-repartition',
    'etl-prep-ebcdic',
    // Schema
    'etl-schema-reorder',
    // Quality
    'etl-quality-check',
    'etl-quality-schema',
    'etl-quality-profiler',
    // Utility
    'etl-util-checkpoint',
    'etl-util-notification',
  ]

  if (PASS_THROUGH.includes(nodeType)) {
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // SOURCES — schema originates here (manual or inferred)
  // ------------------------------------------------------------------
  if (nodeType.startsWith('etl-source-') || nodeType.startsWith('etl-template-')) {
    // Sources don't propagate — they originate.
    // Return whatever schema is already set (manual/inferred), or empty.
    return input.source ? input : EMPTY_SCHEMA
  }

  // ------------------------------------------------------------------
  // TARGETS — consume schema, no output
  // ------------------------------------------------------------------
  if (nodeType.startsWith('etl-target-')) {
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Aggregate
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-aggregate') {
    const agg = parseConfig(config.aggregation_config, { group_by: [], aggregates: [] })
    const outCols = []

    // Group-by columns retain their type from input
    for (const gb of (agg.group_by || [])) {
      const found = findColumn(input, gb)
      outCols.push(found
        ? createColumn(found.name, found.type, found.nullable)
        : createColumn(gb)
      )
    }

    // Aggregate columns — type depends on function
    for (const ag of (agg.aggregates || [])) {
      const alias = ag.alias || `${ag.function}_${ag.column}`
      const fn = (ag.function || 'count').toLowerCase()
      let type = SPARK_TYPES.DOUBLE
      if (fn === 'count' || fn === 'countdistinct' || fn === 'approx_count_distinct') {
        type = SPARK_TYPES.LONG
      } else if (fn === 'first' || fn === 'last') {
        const src = findColumn(input, ag.column)
        type = src ? src.type : SPARK_TYPES.STRING
      } else if (fn === 'collect_list' || fn === 'collect_set') {
        type = SPARK_TYPES.ARRAY
      } else if (fn === 'min' || fn === 'max') {
        const src = findColumn(input, ag.column)
        type = src ? src.type : SPARK_TYPES.DOUBLE
      }
      outCols.push(createColumn(alias, type, true))
    }

    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Join
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-join') {
    const leftCols = [...cols]
    const rightCols = (rightSchema?.columns || []).map(c => ({ ...c }))
    const joinType = config.join_type || 'inner'
    const dropDupKeys = config.drop_duplicate_keys !== false

    // Collect join key names from right side for dedup
    const rightKeyNames = new Set()
    if (dropDupKeys) {
      if (config.right_key) rightKeyNames.add(config.right_key.toLowerCase())
      const extraKeys = parseConfig(config.join_keys, [])
      for (const pair of extraKeys) {
        if (pair.right) rightKeyNames.add(pair.right.toLowerCase())
      }
    }

    const outCols = [...leftCols]

    // Anti join only returns left columns
    if (joinType !== 'anti') {
      for (const rc of rightCols) {
        if (dropDupKeys && rightKeyNames.has(rc.name.toLowerCase())) continue
        // Prefix if name conflict
        const conflict = outCols.some(c => c.name.toLowerCase() === rc.name.toLowerCase())
        outCols.push(createColumn(
          conflict ? `right_${rc.name}` : rc.name,
          rc.type,
          // Outer joins make columns nullable
          joinType === 'left' || joinType === 'right' || joinType === 'full' ? true : rc.nullable
        ))
      }
    }

    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Union
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-union') {
    const byName = config.by_name === true
    if (byName && rightSchema?.columns?.length) {
      // Union of all column names, left order first
      const merged = [...cols]
      const existing = new Set(cols.map(c => c.name.toLowerCase()))
      for (const rc of rightSchema.columns) {
        if (!existing.has(rc.name.toLowerCase())) {
          merged.push(createColumn(rc.name, rc.type, true))
        }
      }
      return createSchema(merged, 'propagated')
    }
    // Positional union — left schema wins
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Intersect / Except
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-intersect' || nodeType === 'etl-transform-except') {
    return createSchema(cols, 'propagated')  // Left schema
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Column Mapper
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-column-mapper') {
    const mappings = parseConfig(config.mapping_config, [])
    const dropUnmapped = config.drop_unmapped === true

    if (!mappings.length) {
      return createSchema(cols, 'propagated')
    }

    const mappedNames = new Set()
    const outCols = []

    for (const m of mappings) {
      if (m.drop) continue  // Dropped column
      const src = findColumn(input, m.source)
      const targetName = m.target || m.source
      const targetType = m.type ? resolveType(m.type) : (src ? src.type : SPARK_TYPES.STRING)
      outCols.push(createColumn(targetName, targetType, src ? src.nullable : true))
      mappedNames.add((m.source || '').toLowerCase())
    }

    // Keep unmapped columns unless dropUnmapped
    if (!dropUnmapped) {
      for (const c of cols) {
        if (!mappedNames.has(c.name.toLowerCase())) {
          outCols.push(createColumn(c.name, c.type, c.nullable))
        }
      }
    }

    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Expression (add derived columns)
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-expression') {
    const exprs = parseConfig(config.expression_config, [])
    const outCols = [...cols]
    for (const expr of exprs) {
      if (expr.column_name) {
        // Check if it overwrites an existing column
        const existIdx = outCols.findIndex(
          c => c.name.toLowerCase() === expr.column_name.toLowerCase()
        )
        const newCol = createColumn(expr.column_name, SPARK_TYPES.STRING, true)
        if (existIdx >= 0) {
          outCols[existIdx] = newCol
        } else {
          outCols.push(newCol)
        }
      }
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Lookup
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-lookup') {
    const returnCols = config.return_columns || []
    const outCols = [...cols]
    for (const rc of returnCols) {
      // Lookup return columns — type unknown at design time
      if (!outCols.some(c => c.name.toLowerCase() === rc.toLowerCase())) {
        outCols.push(createColumn(rc, SPARK_TYPES.STRING, true))
      }
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Window Function
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-window') {
    const outputCol = config.output_column || 'window_result'
    const fn = (config.window_function || '').toLowerCase()
    let type = SPARK_TYPES.LONG
    if (['sum', 'avg', 'min', 'max'].includes(fn)) type = SPARK_TYPES.DOUBLE
    const outCols = [...cols, createColumn(outputCol, type, true)]
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Pivot
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-pivot') {
    if (config.mode === 'unpivot') {
      // Unpivot: group-by cols + name column + value column
      const pivotCol = config.pivot_column || 'variable'
      const valueCol = config.value_column || 'value'
      // Keep non-pivot, non-value columns + add name/value
      const outCols = cols.filter(c =>
        c.name.toLowerCase() !== pivotCol.toLowerCase() &&
        c.name.toLowerCase() !== valueCol.toLowerCase()
      ).map(c => createColumn(c.name, c.type, c.nullable))
      outCols.push(createColumn(pivotCol, SPARK_TYPES.STRING, false))
      outCols.push(createColumn(valueCol, SPARK_TYPES.STRING, true))
      return createSchema(outCols, 'propagated')
    }
    // Pivot mode — can't fully determine columns without data (need distinct values)
    // Return group-by columns + known pivot values if specified
    const pivotValues = config.pivot_values || []
    if (pivotValues.length > 0) {
      const pivotCol = config.pivot_column || ''
      const outCols = cols.filter(c =>
        c.name.toLowerCase() !== pivotCol.toLowerCase()
      ).map(c => createColumn(c.name, c.type, c.nullable))
      for (const pv of pivotValues) {
        outCols.push(createColumn(pv, SPARK_TYPES.DOUBLE, true))
      }
      return createSchema(outCols, 'propagated')
    }
    // Can't infer pivot columns without data — pass through input
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // TRANSFORM: Custom SQL — schema unknown at design time
  // ------------------------------------------------------------------
  if (nodeType === 'etl-transform-custom-sql' || nodeType === 'etl-util-custom-pyspark') {
    // Custom code = manual schema only. Return whatever's set, or empty.
    return input.source === 'manual' ? input : EMPTY_SCHEMA
  }

  // ------------------------------------------------------------------
  // SCHEMA: Select / Drop Columns
  // ------------------------------------------------------------------
  if (nodeType === 'etl-schema-select') {
    const selected = config.columns || []
    const mode = config.mode || 'select'
    if (!selected.length) return createSchema(cols, 'propagated')

    const selSet = new Set(selected.map(s => s.toLowerCase()))
    if (mode === 'select') {
      // Keep only selected
      return createSchema(
        cols.filter(c => selSet.has(c.name.toLowerCase())),
        'propagated'
      )
    }
    // Drop mode — remove selected
    return createSchema(
      cols.filter(c => !selSet.has(c.name.toLowerCase())),
      'propagated'
    )
  }

  // ------------------------------------------------------------------
  // SCHEMA: Rename
  // ------------------------------------------------------------------
  if (nodeType === 'etl-schema-rename') {
    const pairs = parseConfig(config.rename_config, [])
    const renameMap = new Map()
    for (const p of pairs) {
      if (p.left && p.right) {
        renameMap.set(p.left.toLowerCase(), p.right)
      }
    }
    const outCols = cols.map(c => {
      const newName = renameMap.get(c.name.toLowerCase())
      return newName
        ? createColumn(newName, c.type, c.nullable)
        : createColumn(c.name, c.type, c.nullable)
    })
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // SCHEMA: Type Cast
  // ------------------------------------------------------------------
  if (nodeType === 'etl-schema-type-cast') {
    const casts = parseConfig(config.casts, [])
    const castMap = new Map()
    for (const cast of casts) {
      if (cast.column && cast.type) {
        castMap.set(cast.column.toLowerCase(), resolveType(cast.type))
      }
    }
    const outCols = cols.map(c => {
      const newType = castMap.get(c.name.toLowerCase())
      return newType
        ? createColumn(c.name, newType, c.nullable)
        : createColumn(c.name, c.type, c.nullable)
    })
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // SCHEMA: Add Constant Column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-schema-add-constant') {
    const constants = parseConfig(config.columns, [])
    const outCols = [...cols]
    for (const c of constants) {
      if (c.name) {
        outCols.push(createColumn(c.name, resolveType(c.type || 'string'), false))
      }
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // SCHEMA: Flatten / Explode
  // ------------------------------------------------------------------
  if (nodeType === 'etl-schema-flatten') {
    // Can't fully determine flattened schema without knowing nested structure
    // Pass through — schema updates after inference or preview
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // DATA PREP: String Functions, DateTime, Hash — may add output columns
  // ------------------------------------------------------------------
  if (nodeType === 'etl-prep-string-functions' || nodeType === 'etl-prep-datetime') {
    const ops = parseConfig(config.operations, [])
    const outCols = [...cols]
    for (const op of ops) {
      if (op.output && !outCols.some(c => c.name.toLowerCase() === op.output.toLowerCase())) {
        const type = nodeType === 'etl-prep-datetime' ? SPARK_TYPES.TIMESTAMP : SPARK_TYPES.STRING
        outCols.push(createColumn(op.output, type, true))
      }
    }
    return createSchema(outCols, 'propagated')
  }

  if (nodeType === 'etl-prep-hash') {
    const ops = parseConfig(config.operations, [])
    const outCols = [...cols]
    for (const op of ops) {
      if (op.output && !outCols.some(c => c.name.toLowerCase() === op.output.toLowerCase())) {
        outCols.push(createColumn(op.output, SPARK_TYPES.STRING, false))
      }
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: StringIndexer — adds indexed columns
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-string-indexer') {
    const inputCols = config.input_columns || []
    const suffix = config.output_suffix || '_index'
    const outCols = [...cols]
    for (const ic of inputCols) {
      outCols.push(createColumn(`${ic}${suffix}`, SPARK_TYPES.DOUBLE, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: OneHotEncoder — adds encoded vector columns
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-one-hot') {
    const inputCols = config.input_columns || []
    const suffix = config.output_suffix || '_onehot'
    const outCols = [...cols]
    for (const ic of inputCols) {
      outCols.push(createColumn(`${ic}${suffix}`, SPARK_TYPES.VECTOR, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: VectorAssembler — adds features vector column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-vector-assembler') {
    const outputCol = config.output_column || 'features'
    const outCols = [...cols]
    if (!outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.VECTOR, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: Scaler — adds/replaces scaled vector column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-scaler') {
    const outputCol = config.output_column || 'scaled_features'
    const outCols = [...cols]
    if (!outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.VECTOR, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: Bucketizer — adds bucket index column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-bucketizer') {
    const outputCol = config.output_column || ''
    const outCols = [...cols]
    if (outputCol && !outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.DOUBLE, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: Tokenizer — adds token array column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-tokenizer') {
    const outputCol = config.output_column || ''
    const outCols = [...cols]
    if (outputCol && !outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.ARRAY, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: Imputer — adds imputed columns or replaces in-place
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-imputer') {
    const inputCols = config.input_columns || []
    const suffix = config.output_suffix || '_imputed'
    const outCols = [...cols]
    if (suffix) {
      for (const ic of inputCols) {
        const src = findColumn(input, ic)
        outCols.push(createColumn(`${ic}${suffix}`, src ? src.type : SPARK_TYPES.DOUBLE, false))
      }
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // ML PREP: PCA — adds reduced vector column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-ml-pca') {
    const outputCol = config.output_column || 'pca_features'
    const outCols = [...cols]
    if (!outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.VECTOR, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Pass-through output (primary output = same schema)
  // Stats nodes have dual outputs. The primary "Pass-Through" output
  // keeps the original schema. The secondary "Stats" output has a
  // stats-specific schema, but we don't propagate that since it's
  // typically written to a target or inspected, not chained further.
  // ------------------------------------------------------------------
  const STATS_PASS_THROUGH = [
    'etl-stats-summary',
    'etl-stats-percentiles',
    'etl-stats-profile',
    'etl-stats-frequency',
    'etl-stats-correlation',
    'etl-stats-crosstab',
    'etl-stats-covariance',
    'etl-stats-chi-square',
    'etl-stats-histogram',
    'etl-stats-sample',
    'etl-stats-ks-test',
  ]

  if (STATS_PASS_THROUGH.includes(nodeType)) {
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Outlier detection — adds flag column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-outlier-zscore' || nodeType === 'etl-stats-outlier-iqr') {
    const column = config.column || config.value_column || ''
    const flagCol = column ? `${column}_outlier_flag` : 'outlier_flag'
    const outCols = [...cols, createColumn(flagCol, SPARK_TYPES.BOOLEAN, false)]
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Time Series — add computed column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-moving-avg') {
    const valueCol = config.value_column || 'value'
    const maType = (config.ma_type || 'simple').toLowerCase()
    const outCol = `${valueCol}_${maType === 'exponential' ? 'ema' : 'sma'}`
    const outCols = [...cols, createColumn(config.output_column || outCol, SPARK_TYPES.DOUBLE, true)]
    return createSchema(outCols, 'propagated')
  }

  if (nodeType === 'etl-stats-lag-lead') {
    const valueCol = config.value_column || 'value'
    const outCols = [...cols]
    outCols.push(createColumn(`${valueCol}_lag`, SPARK_TYPES.DOUBLE, true))
    outCols.push(createColumn(`${valueCol}_lead`, SPARK_TYPES.DOUBLE, true))
    if (config.compute_change !== false) {
      outCols.push(createColumn(`${valueCol}_change`, SPARK_TYPES.DOUBLE, true))
    }
    return createSchema(outCols, 'propagated')
  }

  if (nodeType === 'etl-stats-rolling-window') {
    const valueCol = config.value_column || 'value'
    const fn = config.function || 'avg'
    const outCol = config.output_column || `${valueCol}_rolling_${fn}`
    const outCols = [...cols, createColumn(outCol, SPARK_TYPES.DOUBLE, true)]
    return createSchema(outCols, 'propagated')
  }

  if (nodeType === 'etl-stats-cumulative') {
    const valueCol = config.value_column || 'value'
    const fn = config.function || 'sum'
    const outCol = config.output_column || `${valueCol}_cumulative_${fn}`
    const outCols = [...cols, createColumn(outCol, SPARK_TYPES.DOUBLE, true)]
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Clustering — adds cluster column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-kmeans' || nodeType === 'etl-stats-bisecting-kmeans') {
    const predCol = config.prediction_column || 'cluster'
    const outCols = [...cols, createColumn(predCol, SPARK_TYPES.INTEGER, false)]
    return createSchema(outCols, 'propagated')
  }

  if (nodeType === 'etl-stats-gmm') {
    const predCol = config.prediction_column || 'cluster'
    const outCols = [...cols]
    outCols.push(createColumn(predCol, SPARK_TYPES.INTEGER, false))
    outCols.push(createColumn(`${predCol}_probability`, SPARK_TYPES.VECTOR, true))
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Classification — adds prediction column
  // ------------------------------------------------------------------
  const CLASSIFIERS = [
    'etl-stats-logistic-regression',
    'etl-stats-random-forest-classifier',
    'etl-stats-gbt-classifier',
    'etl-stats-decision-tree',
    'etl-stats-naive-bayes',
  ]

  if (CLASSIFIERS.includes(nodeType)) {
    const predCol = config.prediction_column || 'prediction'
    const outCols = [...cols]
    outCols.push(createColumn(predCol, SPARK_TYPES.DOUBLE, false))
    outCols.push(createColumn('probability', SPARK_TYPES.VECTOR, true))
    outCols.push(createColumn('rawPrediction', SPARK_TYPES.VECTOR, true))
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Regression — adds prediction column
  // ------------------------------------------------------------------
  const REGRESSORS = [
    'etl-stats-linear-regression',
    'etl-stats-random-forest-regressor',
    'etl-stats-gbt-regressor',
  ]

  if (REGRESSORS.includes(nodeType)) {
    const predCol = config.prediction_column || 'prediction'
    const outCols = [...cols, createColumn(predCol, SPARK_TYPES.DOUBLE, false)]
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Train/Test Split — pass-through (same columns, split rows)
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-train-test-split') {
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Evaluator — pass-through (adds metrics as side output)
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-evaluator') {
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: FP-Growth — pass-through (itemsets and rules are side outputs)
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-fp-growth') {
    return createSchema(cols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: PCA (Stats) — adds PCA features column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-pca') {
    const outputCol = config.output_column || 'pca_features'
    const outCols = [...cols]
    if (!outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.VECTOR, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: Chi-Square Selector — replaces features with selected subset
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-chi-sq-selector') {
    const outputCol = config.output_column || 'selected_features'
    const outCols = [...cols]
    if (!outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.VECTOR, false))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // STATS: LDA (Topic Modeling) — adds topic distribution column
  // ------------------------------------------------------------------
  if (nodeType === 'etl-stats-lda') {
    const outputCol = config.output_column || 'topic_distribution'
    const outCols = [...cols]
    if (!outCols.some(c => c.name.toLowerCase() === outputCol.toLowerCase())) {
      outCols.push(createColumn(outputCol, SPARK_TYPES.VECTOR, true))
    }
    return createSchema(outCols, 'propagated')
  }

  // ------------------------------------------------------------------
  // FALLBACK — unknown node type: pass through
  // ------------------------------------------------------------------
  return createSchema(cols, 'propagated')
}

// =============================================================================
// DAG TRAVERSAL — PROPAGATE SCHEMAS THROUGH THE PIPELINE
// =============================================================================

/**
 * Build adjacency list from ReactFlow edges.
 * @param {Array} edges - ReactFlow edge array
 * @returns {{ children: Map<string, string[]>, parents: Map<string, string[]> }}
 */
function buildAdjacency(edges) {
  const children = new Map()  // nodeId -> [downstream nodeIds]
  const parents = new Map()   // nodeId -> [upstream nodeIds]

  for (const edge of edges) {
    if (!children.has(edge.source)) children.set(edge.source, [])
    children.get(edge.source).push(edge.target)

    if (!parents.has(edge.target)) parents.set(edge.target, [])
    parents.get(edge.target).push(edge.source)
  }

  return { children, parents }
}

/**
 * Topological sort of the DAG (Kahn's algorithm).
 * @param {Array} nodes - ReactFlow node array
 * @param {Map} children - Adjacency list (parent -> children)
 * @param {Map} parents  - Reverse adjacency (child -> parents)
 * @returns {string[]} Node IDs in topological order
 */
function topoSort(nodes, children, parents) {
  const inDegree = new Map()
  for (const node of nodes) {
    const pList = parents.get(node.id) || []
    inDegree.set(node.id, pList.length)
  }

  const queue = []
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id)
  }

  const order = []
  while (queue.length > 0) {
    const id = queue.shift()
    order.push(id)
    for (const child of (children.get(id) || [])) {
      const newDeg = (inDegree.get(child) || 1) - 1
      inDegree.set(child, newDeg)
      if (newDeg === 0) queue.push(child)
    }
  }

  return order
}

/**
 * Get the node type from a ReactFlow node object.
 * Handles both `node.data.type` and `node.data.nodeType` patterns.
 */
function getNodeType(node) {
  return node.data?.type || node.data?.nodeType || node.type || ''
}

/**
 * Determine if a node type has multiple inputs (Join, Union, Intersect, Except, Lookup).
 */
const MULTI_INPUT_TYPES = new Set([
  'etl-transform-join',
  'etl-transform-union',
  'etl-transform-intersect',
  'etl-transform-except',
  'etl-transform-lookup',
])

/**
 * Propagate schemas through the entire pipeline DAG.
 *
 * For each node in topological order:
 *   1. Collect input schema(s) from parent node(s)
 *   2. Compute output schema using node type + config
 *   3. Store inputSchema and outputSchema on the node
 *
 * @param {Array} nodes - ReactFlow node array (will be mutated with schemas)
 * @param {Array} edges - ReactFlow edge array
 * @returns {Array} Updated nodes with inputSchema and outputSchema on node.data
 */
export function propagateSchemas(nodes, edges) {
  if (!nodes?.length) return nodes

  const { children, parents } = buildAdjacency(edges)
  const order = topoSort(nodes, children, parents)
  const nodeMap = new Map(nodes.map(n => [n.id, n]))

  // Schema cache — nodeId -> outputSchema
  const schemaCache = new Map()

  for (const nodeId of order) {
    const node = nodeMap.get(nodeId)
    if (!node) continue

    const nodeType = getNodeType(node)
    const config = node.data?.config || {}
    const parentIds = parents.get(nodeId) || []

    // -----------------------------------------------------------
    // Collect input schema(s)
    // -----------------------------------------------------------
    let inputSchema = EMPTY_SCHEMA
    let rightSchema = null

    if (parentIds.length === 1) {
      inputSchema = schemaCache.get(parentIds[0]) || EMPTY_SCHEMA
    } else if (parentIds.length > 1 && MULTI_INPUT_TYPES.has(nodeType)) {
      // First parent = left/main input, second = right/lookup input
      inputSchema = schemaCache.get(parentIds[0]) || EMPTY_SCHEMA
      rightSchema = schemaCache.get(parentIds[1]) || EMPTY_SCHEMA
    } else if (parentIds.length > 1) {
      // Multiple inputs but not a multi-input node — take first
      inputSchema = schemaCache.get(parentIds[0]) || EMPTY_SCHEMA
    }

    // -----------------------------------------------------------
    // Source nodes: preserve manually set / inferred schemas
    // -----------------------------------------------------------
    if (nodeType.startsWith('etl-source-')) {
      // If the node already has a schema (manual or inferred), keep it
      const existing = node.data?.outputSchema
      if (existing?.columns?.length) {
        inputSchema = existing
        schemaCache.set(nodeId, existing)
        // Store on node data
        node.data = {
          ...node.data,
          inputSchema: EMPTY_SCHEMA,
          outputSchema: existing,
        }
        continue
      }
    }

    // -----------------------------------------------------------
    // Compute output schema
    // -----------------------------------------------------------
    const outputSchema = computeOutputSchema(nodeType, inputSchema, config, rightSchema)

    // Store on node data
    node.data = {
      ...node.data,
      inputSchema,
      outputSchema,
    }

    // Cache for downstream nodes
    schemaCache.set(nodeId, outputSchema)
  }

  return nodes
}

/**
 * Propagate schemas starting from a specific node downstream.
 * More efficient than full propagation when only one node changed.
 *
 * @param {string} startNodeId - Node whose config changed
 * @param {Array} nodes        - Full node array
 * @param {Array} edges        - Full edge array
 * @returns {Array} Updated nodes
 */
export function propagateFromNode(startNodeId, nodes, edges) {
  // For simplicity, do full propagation.
  // The DAG is small (< 200 nodes max) so this is instantaneous.
  return propagateSchemas(nodes, edges)
}
